import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { 
  SavedScenario, 
  SavedSystem, 
  SavedFormat,
  ScenarioInput,
  ScenarioResult,
  SystemAnalysisResult,
  FormatAnalysisResult 
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface ScenarioStore {
  // データ
  scenarios: SavedScenario[]
  systems: SavedSystem[]
  formats: SavedFormat[]
  
  // シナリオ管理
  addScenario: (scenario: Omit<SavedScenario, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateScenario: (id: string, scenario: Partial<SavedScenario>) => void
  removeScenario: (id: string) => void
  getScenario: (id: string) => SavedScenario | undefined
  toggleScenarioFavorite: (id: string) => void
  
  // システム管理
  addSystem: (system: Omit<SavedSystem, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateSystem: (id: string, system: Partial<SavedSystem>) => void
  removeSystem: (id: string) => void
  getSystem: (id: string) => SavedSystem | undefined
  toggleSystemFavorite: (id: string) => void
  
  // フォーマット管理
  addFormat: (format: Omit<SavedFormat, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateFormat: (id: string, format: Partial<SavedFormat>) => void
  removeFormat: (id: string) => void
  getFormat: (id: string) => SavedFormat | undefined
  toggleFormatFavorite: (id: string) => void
  incrementFormatUsage: (id: string) => void
  
  // エクスポート/インポート
  exportData: () => string
  importData: (jsonString: string) => void
  exportScenarioAsText: (id: string) => void
  exportScenarioAsJSON: (id: string) => void
  exportSystemAsJSON: (id: string) => void
  exportFormatAsJSON: (id: string) => void
  
  // ユーティリティ
  clearAll: () => void
  clearScenarios: () => void
  clearSystems: () => void
  clearFormats: () => void
  getScenariosByWorld: (worldId: string) => SavedScenario[]
  getScenariosBySystem: (systemId: string) => SavedScenario[]
  getScenariosByFormat: (formatId: string) => SavedScenario[]
}

// 最大保存数の制限
const MAX_SCENARIOS = 50
const MAX_SYSTEMS = 20
const MAX_FORMATS = 30

export const useScenarioStore = create<ScenarioStore>()(
  persist(
    (set, get) => ({
      scenarios: [],
      systems: [],
      formats: [],
      
      // シナリオ管理
      addScenario: (scenario) => {
        const newScenario: SavedScenario = {
          ...scenario,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          scenarios: [newScenario, ...state.scenarios].slice(0, MAX_SCENARIOS)
        }))
      },
      
      updateScenario: (id, updates) => {
        set(state => ({
          scenarios: state.scenarios.map(s =>
            s.id === id ? { ...s, ...updates, updatedAt: new Date().toISOString() } : s
          )
        }))
      },
      
      removeScenario: (id) => {
        set(state => ({
          scenarios: state.scenarios.filter(s => s.id !== id)
        }))
      },
      
      getScenario: (id) => {
        return get().scenarios.find(s => s.id === id)
      },
      
      toggleScenarioFavorite: (id) => {
        set(state => ({
          scenarios: state.scenarios.map(s =>
            s.id === id ? { ...s, favorite: !s.favorite } : s
          )
        }))
      },
      
      // システム管理
      addSystem: (system) => {
        const newSystem: SavedSystem = {
          ...system,
          id: `sys_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          systems: [newSystem, ...state.systems].slice(0, MAX_SYSTEMS)
        }))
      },
      
      updateSystem: (id, updates) => {
        set(state => ({
          systems: state.systems.map(sys =>
            sys.id === id ? { ...sys, ...updates, updatedAt: new Date().toISOString() } : sys
          )
        }))
      },
      
      removeSystem: (id) => {
        set(state => ({
          systems: state.systems.filter(sys => sys.id !== id)
        }))
      },
      
      getSystem: (id) => {
        return get().systems.find(sys => sys.id === id)
      },
      
      toggleSystemFavorite: (id) => {
        set(state => ({
          systems: state.systems.map(sys =>
            sys.id === id ? { ...sys, favorite: !sys.favorite } : sys
          )
        }))
      },
      
      // フォーマット管理
      addFormat: (format) => {
        const newFormat: SavedFormat = {
          ...format,
          id: `fmt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          formats: [newFormat, ...state.formats].slice(0, MAX_FORMATS)
        }))
      },
      
      updateFormat: (id, updates) => {
        set(state => ({
          formats: state.formats.map(fmt =>
            fmt.id === id ? { ...fmt, ...updates, updatedAt: new Date().toISOString() } : fmt
          )
        }))
      },
      
      removeFormat: (id) => {
        set(state => ({
          formats: state.formats.filter(fmt => fmt.id !== id)
        }))
      },
      
      getFormat: (id) => {
        return get().formats.find(fmt => fmt.id === id)
      },
      
      toggleFormatFavorite: (id) => {
        set(state => ({
          formats: state.formats.map(fmt =>
            fmt.id === id ? { ...fmt, favorite: !fmt.favorite } : fmt
          )
        }))
      },
      
      incrementFormatUsage: (id) => {
        set(state => ({
          formats: state.formats.map(fmt =>
            fmt.id === id 
              ? { ...fmt, usageCount: (fmt.usageCount || 0) + 1 }
              : fmt
          )
        }))
      },
      
      // エクスポート/インポート
      exportData: () => {
        const { scenarios, systems, formats } = get()
        return JSON.stringify({ scenarios, systems, formats }, null, 2)
      },
      
      importData: (jsonString) => {
        try {
          const data = JSON.parse(jsonString)
          set({
            scenarios: data.scenarios || [],
            systems: data.systems || [],
            formats: data.formats || []
          })
        } catch (error) {
          throw new Error('インポートデータの形式が不正です')
        }
      },
      
      exportScenarioAsText: (id) => {
        const scenario = get().scenarios.find(s => s.id === id)
        if (!scenario) return
        
        // テキスト形式でエクスポート
        const text = formatScenarioAsText(scenario)
        downloadTextFile(`scenario_${scenario.result.basicInfo.title}.txt`, text)
      },
      
      exportScenarioAsJSON: (id) => {
        const scenario = get().scenarios.find(s => s.id === id)
        if (!scenario) return
        
        const json = JSON.stringify(scenario, null, 2)
        downloadJSONFile(`scenario_${scenario.result.basicInfo.title}.json`, json)
      },
      
      exportSystemAsJSON: (id) => {
        const system = get().systems.find(s => s.id === id)
        if (!system) return
        
        const json = JSON.stringify(system, null, 2)
        downloadJSONFile(`system_${system.name}.json`, json)
      },
      
      exportFormatAsJSON: (id) => {
        const format = get().formats.find(f => f.id === id)
        if (!format) return
        
        const json = JSON.stringify(format, null, 2)
        downloadJSONFile(`format_${format.name}.json`, json)
      },
      
      // ユーティリティ
      clearAll: () => {
        set({ scenarios: [], systems: [], formats: [] })
      },
      
      clearScenarios: () => {
        set({ scenarios: [] })
      },
      
      clearSystems: () => {
        set({ systems: [] })
      },
      
      clearFormats: () => {
        set({ formats: [] })
      },
      
      getScenariosByWorld: (worldId) => {
        return get().scenarios.filter(s => s.worldId === worldId)
      },
      
      getScenariosBySystem: (systemId) => {
        return get().scenarios.filter(s => s.systemId === systemId)
      },
      
      getScenariosByFormat: (formatId) => {
        return get().scenarios.filter(s => s.formatId === formatId)
      },
    }),
    {
      name: 'scenario-storage',  // 【重要】このキー名は変更禁止
      version: 1,
      storage: createIndexedDBStorage('scenario-storage'),  // 【必須】IndexedDB使用
      partialize: (state) => ({  // 【必須】関数を除外してデータのみ保存
        scenarios: state.scenarios,
        systems: state.systems,
        formats: state.formats
        // 注意: 関数は含めない
      })
    }
  )
)

// ヘルパー関数
function formatScenarioAsText(scenario: SavedScenario): string {
  const result = scenario.result
  
  let text = `# ${result.basicInfo.title}\n\n`
  
  if (result.basicInfo.subtitle) {
    text += `## ${result.basicInfo.subtitle}\n\n`
  }
  
  text += `**ジャンル**: ${result.basicInfo.genre}\n`
  text += `**システム**: ${result.basicInfo.system}\n`
  text += `**難易度**: ${result.basicInfo.difficulty}\n`
  text += `**プレイ時間**: ${result.basicInfo.playTime}\n`
  text += `**プレイヤー人数**: ${result.basicInfo.playerCount}\n`
  if (result.basicInfo.recommendedLevel) {
    text += `**推奨レベル**: ${result.basicInfo.recommendedLevel}\n`
  }
  text += `\n---\n\n`
  
  text += `## 概要\n${result.basicInfo.summary}\n\n`
  text += `## イントロダクション\n${result.basicInfo.introduction}\n\n`
  
  // シーン
  if (result.scenes.length > 0) {
    text += `## シーン\n\n`
    result.scenes.forEach(scene => {
      text += `### シーン${scene.id}: ${scene.title}\n`
      text += `**タイプ**: ${scene.type}\n`
      text += `**目的**: ${scene.purpose}\n\n`
      text += `${scene.description.initial}\n\n`
      
      if (scene.description.details.length > 0) {
        text += `**詳細**:\n`
        scene.description.details.forEach(detail => {
          text += `- ${detail}\n`
        })
        text += `\n`
      }
    })
  }
  
  // NPC
  if (result.npcs.length > 0) {
    text += `## NPC\n\n`
    result.npcs.forEach(npc => {
      text += `### ${npc.name} (${npc.role})\n`
      text += `${npc.appearance.brief}\n`
      text += `**動機**: ${npc.personality.motivation}\n\n`
    })
  }
  
  // アイテム
  if (result.items.length > 0) {
    text += `## アイテム\n\n`
    result.items.forEach(item => {
      text += `### ${item.name} (${item.type})\n`
      text += `${item.description.appearance}\n`
      text += `**重要度**: ${item.description.significance}\n\n`
    })
  }
  
  // GMノート
  if (result.gmNotes) {
    text += `## GMノート\n\n`
    text += `### 準備\n`
    text += `**必須**: ${result.gmNotes.preparation.required.join(', ')}\n`
    text += `**推奨**: ${result.gmNotes.preparation.recommended.join(', ')}\n\n`
    
    text += `### ペーシング\n`
    text += `${result.gmNotes.runningTips.pacing}\n\n`
  }
  
  return text
}

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function downloadJSONFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}